import React, { useMemo, useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import styled from 'styled-components';
import { Calendar as CalendarIcon } from 'react-feather';
import { formatDateDDMMM, formatDateYYYYMMDD } from '../helpers/utils';
import { Button } from 'reactstrap';

type View = 'century' | 'decade' | 'year' | 'month';
type Action = 'prev' | 'prev2' | 'next' | 'next2' | 'onChange' | 'drillUp' | 'drillDown';
type OnArgs = {
  action: Action;
  activeStartDate: Date | null;
  value: Value;
  view: View;
};

type Range<T> = [T, T];
type ValuePiece = Date | null;
type Value = ValuePiece | Range<ValuePiece>;

export interface DateClassNames {
  [key: string]: string[]; 
}

interface ApplicationsCalendarProps {
  dateClassNames: DateClassNames;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  showFullCalendarInitially?: boolean;
  showNavigationButtons?: boolean;
  autoHideOnDateSelect?: boolean;
}

export const combineDateClassNames = (
  plannedDateClassNames: DateClassNames,
  executedDateClassNames: DateClassNames
): DateClassNames => {
  const combinedClassNames: DateClassNames = { ...plannedDateClassNames };

  // Iterate through executed dates and combine with planned dates
  Object.entries(executedDateClassNames).forEach(([date, classNames]) => {
    if (date in combinedClassNames) {
      // Date exists in both - concatenate arrays
      combinedClassNames[date] = [...new Set([...combinedClassNames[date], ...classNames])];
    } else {
      // Date only in executed - add it
      combinedClassNames[date] = classNames;
    }
  });

  return combinedClassNames;
};

const CalendarWrapper = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  margin-bottom: 2rem;
  width: 100%;
  border: 1px solid rgb(10, 36, 65);
  border-radius: 0.5rem;
  max-width: 800px;
  margin: 0 auto;
  
  .react-calendar {
    width: 100%;
    border: 1px solid #ccc !important;  // Force border to display
    border-radius: 8px;
    padding: 1rem;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;  // Force shadow to display

    .weekend {
      color: #999;
    }
  }
`;

// Add this CSS to your styles
const styles = `
  .selected-date {
    position: relative;
    font-weight: bold;
    color: rgb(22, 14, 246);
    background: none !important;  /* Override default blue background */
  }
  .selected-date abbr {
    position: relative;
    z-index: 1;
    color: rgb(22, 14, 246) !important;
 /*   color: inherit !important;   Override default white text */
  }
  .selected-date::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: var(--calendar-marker-size, clamp(1.9rem, 6.8vw, 2.2rem));
    height: var(--calendar-marker-size, clamp(1.9rem, 6.8vw, 2.2rem));
    border: 2px solid rgb(22, 14, 246);
    color: rgb(22, 14, 246) !important;
    border-radius: 50%;
  }
  .today {
    position: relative;
    font-weight: bold;
    color: rgb(241, 241, 246);
    background: none !important;  /* Override default blue background */
  }
  .today abbr {
    position: relative;
    z-index: 1;
    color: rgb(234, 233, 238) !important;
 /*   color: inherit !important;   Override default white text */
  }
  .today::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: var(--calendar-marker-size, clamp(1.9rem, 6.8vw, 2.2rem));
    height: var(--calendar-marker-size, clamp(1.9rem, 6.8vw, 2.2rem));
    border: 2px solid rgb(22, 14, 246);
    color: rgb(255, 255, 255) !important;
    background-color: rgb(22, 14, 246);
    border-radius: 50%;
  }
`;

export const ApplicationsCalendar: React.FC<ApplicationsCalendarProps> = ({
  dateClassNames,
  selectedDate = new Date(),
  onDateChange,
  showFullCalendarInitially = true,
  showNavigationButtons = true,
  autoHideOnDateSelect = false
}) => {
  const [isCalendarVisible, setIsCalendarVisible] = useState(showFullCalendarInitially);
  const [activeStartDate, setActiveStartDate] = useState<Date>(selectedDate);
  const [selectedDateCalendar, setSelectedDateCalendar  ] = useState<Date>(selectedDate);

  // When we change a date in the calendar, we update the selected date and the active start date
  //
  const updateSelectedDate = (date: Date) => {
    setSelectedDateCalendar(date);
    setActiveStartDate(date);
    onDateChange(date);
    if (autoHideOnDateSelect) {
      setIsCalendarVisible(false);
    }
  };

  // Create a list of dates from the dateClassNames
  //
  const dates = useMemo(() => {
    return Object.keys(dateClassNames)
      .map(dateStr => new Date(dateStr))
      .filter(date => !isNaN(date.getTime()))  // Filter out invalid dates
      .sort((a, b) => a.getTime() - b.getTime());  // Sort chronologically
  }, [dateClassNames]);
  

  const findPreviousDate = () => {
    const currentTime = selectedDate.getTime();
    const prevDate = [...dates]
      .reverse()
      .find(date => date.getTime() < currentTime);
    if (prevDate) {
        updateSelectedDate(prevDate);
    }
  };

  const findNextDate = () => {
    const currentTime = selectedDate.getTime();
    const nextDate = dates.find(date => date.getTime() > currentTime);
    if (nextDate) {
        updateSelectedDate(nextDate);
    }
  };

  const goToToday = () => {
    updateSelectedDate(new Date());
  };

 
  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    const isSelected = selectedDateCalendar && 
      date.getDate() === selectedDateCalendar.getDate() && 
      date.getMonth() === selectedDateCalendar.getMonth() && 
      date.getFullYear() === selectedDateCalendar.getFullYear();

    const today = new Date();
    const isToday = selectedDate && 
      date.getDate() === today.getDate() && 
      date.getMonth() === today.getMonth() && 
      date.getFullYear() === today.getFullYear();


    if (view === 'month') {
      const dateStr = formatDateYYYYMMDD(date) || '';
      let classNames = (dateClassNames[dateStr] || []).join(' ');
      
      if (isSelected) {
        classNames = classNames + ' selected-date';
      }
      if (isToday) {
        classNames = classNames + ' today';
      }

      // Tuesday and Thursday
      const day = date.getDay();
      if (day === 2 || day === 4) {
        classNames = classNames + ' highlight-day';
      }
      
      return classNames;
    }
    return null;


  };

  const handleDateChange = (value: Value) => {
    if (value instanceof Date) {
        updateSelectedDate(value);
    }
  };  

  const handleNavigate = ({ activeStartDate }: OnArgs) => {
    if (activeStartDate) {
      setActiveStartDate(activeStartDate);
    }
  };

  const toggleCalendar = () => {
    setIsCalendarVisible(!isCalendarVisible);
  };

  const CommandButton = ({onClick, children}: {onClick: () => void, children: React.ReactNode}) => {
    return (
      <Button 
        color="primary" 
        outline 
        onClick={(e) => {   
          e.currentTarget.blur();
          onClick();
        }}
        className="navigation-button"
      >
        {children}
      </Button> 
    );
  };

  const currentYear = new Date().getFullYear();
  const minDate = new Date(currentYear, 0, 1);      // Month is 0-based (0 = January)
  const maxDate = new Date(currentYear, 11, 31);    // 11 = December

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    setSelectedDateCalendar(selectedDate);
    setActiveStartDate(selectedDate);
  }, [selectedDate]);

  return (
    <div>
        <div className="calendar-container mb-4">
          {showNavigationButtons && (
            <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
              <CommandButton onClick={findPreviousDate}>
                  <i className="fas fa-chevron-left" /> Précédent
              </CommandButton>

              <CommandButton onClick={goToToday}>
                  Aujourd&apos;hui
              </CommandButton>

              <CommandButton onClick={findNextDate}>
                  Suivant <i className="fas fa-chevron-right" />
              </CommandButton>
            </div>
          )}
     
            <CalendarWrapper $isVisible={isCalendarVisible} >
                <Calendar
                      onChange={handleDateChange}
                      value={selectedDateCalendar}
                      tileClassName={tileClassName}
                      className="rounded shadow-sm"
                      activeStartDate={activeStartDate}
                      onActiveStartDateChange={handleNavigate}
                      nextLabel="›"
                      next2Label="»"
                      prevLabel="‹"
                      prev2Label="«"
                      formatShortWeekday={(locale, date) => {
                      const day = date.getDay();
                      const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                      return weekDays[day];
                      }}
                      minDate={minDate}
                      maxDate={maxDate}
                  />
            </CalendarWrapper>

            <div className="d-flex justify-content-center align-items-center mt-2 ms-2" onClick={toggleCalendar}>
                <CommandButton onClick={toggleCalendar}>
                    {formatDateDDMMM(selectedDate)}  <CalendarIcon size={20} style={{marginLeft: '4rem'}}/>
                </CommandButton>
            </div>

        </div>

    </div>
  );
};